'use client';

import { useRadioGroup } from '@react-aria/radio';
import { useRadioGroupState } from '@react-stately/radio';

import { type VariantProps } from 'tailwind-variants';

import { FormDescription } from '../../internal/components/form/FormDescription';
import { FormLabel } from '../../internal/components/form/FormLabel';
import { filterProps } from '../../internal/utils/filterProps';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { type RadioProps } from '../radio/Radio';
import { RadioGroupContext } from './RadioGroupContext';

type Variant = VariantProps<typeof radioGroupStyles>;
interface RadioGroupBaseProps {
  /**
   * The label for the `RadioGroup`.
   */
  label?: string;
  /**
   * The current selected value (controlled).
   */
  value?: string;
  /**
   * The default selected value (uncontrolled).
   */
  defaultValue?: string;
  /**
   * Handler that is called when the selected value changes.
   */
  onChange?: (value: string) => void;
  /**
   * Whether all radios in the group are disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the group is invalid.
   */
  isInvalid?: Variant['isInvalid'];
  /**
   * Secondary content displayed after the label (e.g., "Optional").
   */
  subLabel?: string;
  /**
   * Content rendered at the end of the label row (e.g., character count).
   */
  endLabel?: string;
  /**
   * Whether one radio in the group must be selected before form submission.
   */
  isRequired?: boolean;
  /**
   * Provide the user with context or feedback for the `RadioGroup`.
   * Shown below the group. Replaced by `errorMessage` when `isInvalid` is true and `errorMessage` is set.
   */
  description?: string;
  /**
   * The error message to display when `isInvalid` is true. Replaces `description` when shown.
   */
  errorMessage?: string;
  /**
   * Whether the `RadioGroup` is in a success state.
   */
  isSuccess?: boolean;
  /**
   * Success message shown when `isSuccess` is true. Replaces `description` when shown.
   */
  successMessage?: string;
  /**
   * Handler called when the focus state changes within the `RadioGroup`.
   */
  onFocusChange?: (isFocused: boolean) => void;
  /**
   * The color of the `RadioGroup` label text.
   * @default 'primary'
   */
  color?: Variant['color'];
  /**
   * The orientation of the `RadioGroup`.
   * @default 'vertical'
   */
  orientation?: Variant['orientation'];
  /**
   * The `<form>` element to associate the `RadioGroup` with. The value of this attribute must be the id of a `<form>` in the same document.
   */
  form?: string;
  /**
   * The name of the `RadioGroup`, used when submitting an HTML form.
   */
  name?: string;
  /**
   * The size of all child `Radio` components. Propagated via context; individual radios may override this.
   * @default 'md'
   */
  size?: RadioProps['size'];
  /**
   * The icon position of all child `Radio` components. Propagated via context; individual radios may override this.
   * @default 'left'
   */
  iconPosition?: RadioProps['iconPosition'];
  /**
   * The vertical alignment of all child `Radio` icons. Propagated via context; individual radios may override this.
   * @default 'start'
   */
  alignIcon?: RadioProps['alignIcon'];
}

const radioGroupClasses = {
  root: 'zds-radiogroup-root',
  label: 'zds-radiogroup-label',
  description: 'zds-radiogroup-description',
} as const;

const radioGroupStyles = tv({
  slots: {
    root: [radioGroupClasses.root, 'flex'],
    label: [radioGroupClasses.label, 'flex', 'items-center'],
    description: [radioGroupClasses.description],
  },
  variants: {
    color: {
      primary: {
        label: 'text-primary',
      },
      white: {
        label: 'text-static-white-main',
      },
    },
    isInvalid: {
      true: {
        label: 'text-error-main',
      },
    },
    isDisabled: {
      true: {
        label: 'disabled-foreground',
      },
    },
    orientation: {
      vertical: { root: 'flex-col z--radioGroup-gap-vertical' },
      horizontal: {
        root: 'flex-row flex-wrap z--radioGroup-gap-horizontal',
        label: 'basis-full',
        description: 'basis-full',
      },
    },
  },
});

type RadioGroupProps = ComponentProps<RadioGroupBaseProps, 'div'>;

type RadioGroupComponent = Component<RadioGroupBaseProps, 'div'>;

const RadioGroup: RadioGroupComponent = (props) => {
  const [localProps, radioGroupRaProps] = filterProps(props, ['useRadioGroup']);

  // Component-specific props + restLocalProps for the DOM element
  const {
    children,
    subLabel,
    endLabel,
    isSuccess,
    successMessage,
    color = 'primary',
    size,
    iconPosition,
    alignIcon,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  // Hook-claimed props also needed for rendering
  const {
    label,
    description,
    errorMessage,
    isDisabled,
    isRequired,
    orientation = 'vertical',
  } = props;

  const state = useRadioGroupState({ ...radioGroupRaProps, orientation });

  const {
    radioGroupProps,
    labelProps,
    descriptionProps,
    errorMessageProps,
    isInvalid,
  } = useRadioGroup({ ...radioGroupRaProps, orientation }, state);

  const styles = radioGroupStyles({
    color,
    isInvalid,
    isDisabled,
    orientation,
  });

  const descriptionAriaProps =
    isInvalid && errorMessage ? errorMessageProps : descriptionProps;

  return (
    <div
      {...mergeProps(restLocalProps, radioGroupProps)}
      ref={ref}
      className={styles.root({ className })}
    >
      <FormLabel
        label={label}
        subLabel={subLabel}
        isRequired={isRequired}
        isDisabled={isDisabled}
        endLabel={endLabel}
        color={color}
        slotProps={{ label: { ...labelProps } }}
        className={styles.label()}
      />

      <RadioGroupContext.Provider
        value={{ state, color, size, iconPosition, alignIcon }}
      >
        {children}
      </RadioGroupContext.Provider>

      <FormDescription
        {...descriptionAriaProps}
        color={color}
        description={description}
        errorMessage={errorMessage}
        successMessage={successMessage}
        isInvalid={isInvalid}
        isSuccess={isSuccess}
        isDisabled={isDisabled}
        className={styles.description()}
      />
    </div>
  );
};

RadioGroup.displayName = 'RadioGroup';

export {
  RadioGroup,
  type RadioGroupProps,
  type RadioGroupBaseProps,
  radioGroupClasses,
};
